import React, { FC, ChangeEvent, useState } from 'react';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Form } from 'formik';

import { ForgotPassword, SignUp } from 'Links';
import { useNotify } from 'hooks';
import {
  ActionContainer,
  SubmitButton,
  Layouts,
  ValidatedTextField,
  FormikEffect,
  VisibilityAdornment,
} from 'components';
import { COGNITO_CHALLENGE_NAME_NEW_PASSWORD_REQUIRED } from 'features/auth/constants';

import { Main, ClientSidebarHeader } from '../components';
import { SignInSchema, ISignInValues } from '../validations';
import { IAuthContainerProps } from '../types';
import RequireNewPassword from './RequireNewPassword';

const useStyles = makeStyles(theme => ({
  button: {
    padding: 0,
    marginTop: theme.spacing(1),
  },
}));

const SignIn: FC<IAuthContainerProps> = ({ authData, updateUsername, updatePassword }) => {
  const [working, setWorking] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [tempCognitoUser, setTempCognitoUser] = useState<CognitoUser>(null);

  const { t } = useTranslation();
  const notify = useNotify();
  const classes = useStyles();

  const initialValues: ISignInValues = {
    username: authData.username,
    password: '',
  };

  function handleChange({ username, password }: ISignInValues) {
    updateUsername(username.toLowerCase());
    updatePassword(password);
  }

  async function onSubmit(values: ISignInValues) {
    setWorking(true);
    const sanitizedValues = { ...values, username: values.username.toLowerCase().trim() };

    try {
      const user = await Auth.signIn(sanitizedValues);
      if (user.challengeName === COGNITO_CHALLENGE_NAME_NEW_PASSWORD_REQUIRED) {
        setTempCognitoUser(user);
      }
    } catch (error) {
      setWorking(false);
      notify.error(error);
    }
  }

  function renderSidebar() {
    return (
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={SignInSchema}>
        <Form>
          <FormikEffect onChange={handleChange} delay={100} />
          <ClientSidebarHeader i18nKey="auth:sidebarHeader" />

          <ValidatedTextField
            name="username"
            i18nKey="user:email"
            onChange={(
              event: ChangeEvent<HTMLInputElement>,
              onChange: FieldProps['field']['onChange']
            ) => {
              event.target.value = event.target.value.trim();
              onChange(event);
            }}
          />
          <ValidatedTextField
            name="password"
            type={passwordVisible ? 'text' : 'password'}
            i18nKey="user:password"
            InputProps={{
              endAdornment: (
                <VisibilityAdornment
                  visible={passwordVisible}
                  onToggle={() => setPasswordVisible(!passwordVisible)}
                />
              ),
            }}
          />

          <Button component={ForgotPassword} className={classes.button}>
            {t('auth:signInForgotPassword')}
          </Button>

          <ActionContainer>
            <SubmitButton working={working} text={t('auth:signInButton')} />
            <Button component={SignUp} fullWidth>
              {t('auth:signInSignUp')}
            </Button>
          </ActionContainer>
        </Form>
      </Formik>
    );
  }

  if (tempCognitoUser) {
    return <RequireNewPassword cognitoUser={tempCognitoUser} />;
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
    />
  );
};

export default SignIn;
